import {AsyncPipe, NgIf} from "@angular/common";
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatOption} from "@angular/material/autocomplete";
import {MatButtonModule} from "@angular/material/button";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatSelect} from "@angular/material/select";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {OccupancyType} from "@features/leasing/leasing.model";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {toIsoDateEndOfDay} from "@ui-kit/framework/util/static/date-utils";
import {Subject} from "rxjs";
import {SurveyService} from "../../survey.service";
import {AudienceCategory, CreateSurveyRequest, Survey} from "../../surveys.model";

@Component({
  selector: 'app-create-survey-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatButtonModule,
    NgIf,
    FlexModule,
    MatOption,
    MatSelect,
    AsyncPipe,
    MatProgressBar
  ],
  templateUrl: './create-survey-dialog.component.html'
})
export class CreateSurveyDialogComponent implements OnInit, OnDestroy {
  private _clearOnDestroy$ = new Subject<void>();

  protected readonly OccupancyType = OccupancyType;
  protected readonly AudienceCategory = AudienceCategory;
  protected loading$ = this.loadingService.loading$;
  protected surveyForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private surveyService: SurveyService,
    private loadingService: LoadingService,
    private dialogRef: MatDialogRef<CreateSurveyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { offering: any, clearOnDestroy$?: Subject<void> }
  ) {
    this.surveyForm = this.fb.group({
      name: ['', Validators.required],
      displayTitle: ['', Validators.required],
      audience: ['', Validators.required],
      startDate: [null, Validators.required],
      endDate: [null],
      allowMultiSubmission: [false]
    });
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  ngOnInit(): void {
    if (this.data.clearOnDestroy$) {
      this._clearOnDestroy$ = this.data.clearOnDestroy$;
    }
  }

  onSubmit(): void {
    if (this.surveyForm.valid) {
      const formValue = this.surveyForm.value;
      const request: CreateSurveyRequest = {
        name: formValue.name,
        displayTitle: formValue.displayTitle,
        audience: formValue.audience,
        startDate: formValue.startDate,
        endDate: formValue.endDate ? toIsoDateEndOfDay(formValue.endDate)! : undefined,
        allowMultiSubmission: formValue.allowMultiSubmission ? "true" : "false"
      };

      this.surveyService.createSurvey(request)
        .pipe(this.loadingService.withLoader(this._clearOnDestroy$))
        .subscribe({
          next: (survey: Survey) => {
            this.dialogRef.close(survey);
          },
          error: err => {
            console.error('Error creating survey:', err);
          }
        });
    }
  }

  onCancel(): void {
    this.ngOnDestroy();
    this.dialogRef.close();
  }
}
